import { assetUrl } from '@/utils/assets';
export const DEFAULT_NAME = 'Bigfish';

// ─── Tab switching helper ─────────────────────────────────────────────────────
export function emitTabSwitch(tabKey: string) {
  window.dispatchEvent(new CustomEvent('amp:switch-tab', { detail: tabKey }));
}

// ─── Header Navigation ───────────────────────────────────────────────────────
export interface HeaderTab {
  key: string;
  label: string;
}

export interface SubNavLink {
  label: string;
  href: string;
}

export const HEADER_TABS: HeaderTab[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'agreement', label: 'Specification' },
];

export const SUB_NAV_LINKS: SubNavLink[] = [
  { label: 'Overview', href: '#overview' },
  { label: 'Principles', href: '#why' },
  { label: 'Know Your Agent', href: '#know-your-agent' },
  { label: 'Wallet Role', href: '#role-value' },
  { label: 'Scenarios', href: '#scenarios' },
  { label: 'Partners', href: '#ecosystem' },
  { label: 'Collaborate', href: '#open-source' },
];

// ─── Know Your Agent ─────────────────────────────────────────────────────────
export const KNOW_YOUR_AGENT = {
  title: 'Know Your Agent at Every Step',
  description:
    'Every AMP-connected Agent is verified before access, continuously assessed in action, and trusted for every payment.',
  items: [
    {
      title: '01 Verify before\nonboarding',
      description:
        "Verify each agent's identity, responsible entity, and permitted scope.",
      lottieName: 'kyaVerifyBeforeOnboarding',
    },
    {
      title: '02 Enforce authorized\nscope',
      description:
        'Authenticate each request and keep every action within the authorized intent.',
      lottieName: 'kyaEnforceAuthorizedScope',
    },
    {
      title: '03 Compliance &\nrisk management',
      description:
        'Continuously assess agent activity and restrict, suspend, or revoke access when risks arise.',
      lottieName: 'kyaComplianceRiskManagement',
    },
  ],
  bottomImage: assetUrl('/assets/alipayplusGudie.png'),
};

// ─── Hero Section ────────────────────────────────────────────────────────────
export const HERO_CONTENT = {
  title: 'Agentic Mobile Protocol',
  description:
    'Agentic Mobile Protocol (AMP) is an open-source, application-layer trust protocol that opens digital wallet capabilities to AI agents, making every agent-facilitated payment authorizable, executable, verifiable, and traceable across existing payment infrastructure.',
  heroImage: assetUrl(
    '/assets/PSANHcH-42BCF615-F73C-4422-A19D-2C4195E6FDCD.jpg',
  ),
};

export const HERO_CARDS = [
  {
    tag: 'Learn',
    description:
      'Explore the protocol overview, design principles, core concepts, and supported payment scenarios.',
    buttonLabel: 'Get Started',
    buttonHref: '#',
    buttonTab: 'agreement' as const,
    buttonType: 'secondary' as const,
  },
  {
    tag: 'Implement',
    description:
      'Visit the GitHub repository to access specifications and reference implementations.',
    buttonLabel: 'View on GitHub',
    buttonHref: 'https://github.com/ant-intl/AMP',
    buttonType: 'primary' as const,
  },
];

// ─── Lottie Animation File Mapping ────────────────────────────────────────────
export const LOTTIE_FILENAME_MAP: Record<string, string> = {
  intentionVerifiable: assetUrl('/assets/ZjSyfZO-1_yitukeyanzheng.json'),
  ecologicalCompatibility: assetUrl('/assets/Q4LLxwN-2_shengtaijianrong.json'),
  openInteroperability: assetUrl('/assets/aEvQxqE-3_kaifanghucaozuo.json'),
  securityPrivacy: assetUrl('/assets/H6DuhR6-4_anquanyinsiyouxian.json'),
  kyaVerifyBeforeOnboarding: assetUrl(
    '/assets/_01_Verify_before__onboarding.json',
  ),
  kyaEnforceAuthorizedScope: assetUrl(
    '/assets/_02_Enforce_authorized__scope.json',
  ),
  kyaComplianceRiskManagement: assetUrl(
    '/assets/03_Compliance____Risk_Management.json',
  ),
};

// ─── Design Principles ───────────────────────────────────────────────────────
export const DESIGN_PRINCIPLES = {
  title: 'Designed for trusted agentic payments',
  description:
    'AMP grounds agent-mediated payments in verifiable user intent, builds trust on top of existing payment infrastructure, and enables ecosystem participants to collaborate through a single open protocol.',
  items: [
    {
      name: 'Verifiable intent',
      icon: assetUrl('/assets/vYtlgcW-Frame-2147225754.svg'),
      lottieName: 'intentionVerifiable',
      description:
        'Explicit, non-repudiable user intent ensures every transaction is verifiable, traceable, and tamper-proof.',
    },
    {
      name: 'Low-friction integration',
      icon: assetUrl('/assets/ccGygKe-Frame-2147225745.svg'),
      lottieName: 'ecologicalCompatibility',
      description:
        'Enable seamless integration among all AMP participants while minimizing changes to their existing infrastructure.',
    },
    {
      name: 'Open & interoperable',
      icon: assetUrl('/assets/wuqfYxm-Frame-2147225755.svg'),
      lottieName: 'openInteroperability',
      description:
        'Enable agents, wallets, and payment providers to connect and collaborate across platforms through one shared protocol.',
    },
    {
      name: 'Security & privacy first',
      icon: assetUrl('/assets/HdCwm6O-Frame-2147225752.svg'),
      lottieName: 'securityPrivacy',
      description:
        'Security and privacy are built in by design, with authorization, identity verification, and end-to-end encryption across the transaction flow.',
    },
  ],
};

// ─── Scenarios Section ────────────────────────────────────────────────────────
export interface ScenarioStep {
  step: number;
  title: string;
  description: string;
}

export interface ScenarioTab {
  key: string;
  label: string;
  intro: string;
  introBold: string;
  steps: ScenarioStep[];
  pattern: 'real-time' | 'intent';
}

export const SCENARIOS: ScenarioTab[] = [
  {
    key: 'real-time',
    label: 'Direct Payment',
    introBold: 'Direct Payment (Human-Present)',
    intro:
      'The user is present to select products, review order details, and complete payment in real-time.',
    steps: [
      {
        step: 1,
        title: 'Define purchase',
        description: 'Tell the AI agent what you want to buy.',
      },
      {
        step: 2,
        title: 'Review options and choose',
        description:
          'Browse the product or service recommendations prepared by the agent and choose your preferred option.',
      },
      {
        step: 3,
        title: 'Review the order',
        description:
          'Check the items, delivery information, and payment method.',
      },
      {
        step: 4,
        title: 'Verify and approve',
        description: 'Authenticate and approve the exact payment amount.',
      },
      {
        step: 5,
        title: 'Get payment results',
        description:
          'Get the payment confirmation and order status from the agent.',
      },
    ],
    pattern: 'real-time',
  },
  {
    key: 'intent',
    label: 'Delegated Payment',
    introBold: 'Delegated Payment (Human-Not-Present)',
    intro:
      'The user delegates the shopping goal and authorizes the AI agent to place an order and pay automatically within the scope.',
    steps: [
      {
        step: 1,
        title: 'Set task',
        description:
          'Describe your shopping objective, preferences, and constraints.',
      },
      {
        step: 2,
        title: 'Review authorization limits',
        description:
          'Review the budget, validity period, payment method, and other limits before authorizing the task.',
      },
      {
        step: 3,
        title: 'Verify and authorize',
        description:
          'Authenticate and authorize the agent to act within the defined limits.',
      },
      {
        step: 4,
        title: 'Agent acts within scope',
        description:
          'The agent monitors availability, places the order, and completes the payment automatically within your defined limits.',
      },
      {
        step: 5,
        title: 'Receive task notifications',
        description:
          'Receive payment and order notifications once the task is complete.',
      },
    ],
    pattern: 'intent',
  },
];

// ─── Role Value Section ──────────────────────────────────────────────────────
export const ROLE_VALUE = {
  title: 'Digital wallets in agentic payments',
  description:
    'AMP enables digital wallets to verify user intent, keep agent-initiated payments within authorized limits, and give users ongoing visibility and control.',
  cards: [
    {
      image: assetUrl(
        '/assets/eXK51zP-iwEcAqNqcGcDAQTRBIwF0QSMBrA98lEwwrLuewo7UO2CqcYAB9MAAAABbLpNDAgACaJpbQoAC9IAAMRr.jpg',
      ),
      tag: 'Before AI agent activity',
      title: 'User-confirmed authorization',
      description:
        'The wallet verifies the user-authorized intent and payment scope before agent-initiated payments execute.',
    },
    {
      image: assetUrl('/assets/sRME6ER-Frame-2147230462.jpg'),
      tag: 'During and after AI agent activity',
      title: 'Ongoing visibility and control',
      description:
        'The wallet enables users to monitor active authorizations, budget usage, and payment history, with the flexibility to modify limits or revoke access instantly.',
    },
  ],
};

// ─── Ecosystem Partners ──────────────────────────────────────────────────────
export interface PartnerLogo {
  src: string;
  /** 统一引用 @2x 高清图，src 与 srcSet 指向同一资源 */
  srcSet: string;
  alt: string;
}

export interface PartnerRow {
  key: string;
  logos: PartnerLogo[];
  scroll?: boolean;
  large?: boolean;
}

const WALLENT_LOGO_BASE = '/assets/wallentLogo';

function logo(dir: string, fileName: string, alt?: string): PartnerLogo {
  const url = assetUrl(`${WALLENT_LOGO_BASE}/${dir}/${fileName}`);
  return {
    src: url,
    srcSet: `${url} 2x`,
    alt:
      alt ??
      fileName
        .replace(/^\d+-/, '')
        .replace(/\.png$/, '')
        .replace(/@2x$/, '')
        .replace(/-APlus$/g, ''),
  };
}

export const ECOSYSTEM = {
  title: 'Trusted by ecosystem partners',
  description:
    'AMP provides a shared foundation of trust and a common payment language, enabling ecosystem partners to build on a unified protocol.',
  rows: [
    {
      key: 'deck-main',
      logos: [logo('deck', 'AlipayPlus@2x.png')],
      large: true,
    },
    {
      key: 'deck-sub',
      logos: [logo('deck', 'VISA@2x.png'), logo('deck', 'mastercard@2x.png')],
    },
    {
      key: 'nation-1',
      scroll: true,
      logos: [
        logo('nation', '1-Alipay@2x.png'),
        logo('nation', '2-AlipayHK-APlus@2x.png'),
        logo('nation', '3-Akulaku PayLater-APlus@2x.png'),
        logo('nation', '4-barq@2x.png'),
        logo('nation', '5-bigpay@2x.png'),
        logo('nation', '6-BillEase@2x.png'),
        logo('nation', '7-bluecode@2x.png'),
        logo('nation', '8-Boost-APlus@2x.png'),
        logo('nation', '9-BPI-APlus@2x.png'),
        logo('nation', '10-DOKU-APlus@2x.png'),
        logo('nation', '11-DANA-APlus@2x.png'),
        logo('nation', '12-EZ-Link-APlus@2x.png'),
        logo('nation', '13-GCash-APlus@2x.png'),
        logo('nation', '14-GrabPay@2x.png'),
        logo('nation', '15-Frame 2117130540@2x.png'),
        logo('nation', '16-HelloMoney-APlus@2x.png'),
        logo('nation', '17-Hipay@2x.png'),
        logo('nation', '18-idram@2x.png'),
        logo('nation', '19-iFAST Global Bank@2x.png'),
        logo('nation', '20-KPlus@2x.png', 'K+'),
        logo('nation', '21-Kakao Pay-APlus@2x.png'),
        logo('nation', '22-Kaspi.kz@2x.png'),
        logo('nation', '23-kredivo@2x.png'),
        logo('nation', '24-Linepay@2x.png'),
        logo('nation', '25-Maya@2x.png'),
        logo('nation', '26-MoMo@2x.png'),
        logo('nation', '27-M pay@2x.png'),
        logo('nation', '28-MyBank@2x.png'),
        logo('nation', '29-MyPB@2x.png'),
      ],
    },
    {
      key: 'nation-2',
      scroll: true,
      logos: [
        logo('nation', '30-Naverpay@2x.png'),
        logo('nation', '31-Nayapay@2x.png'),
        logo('nation', '32-OCBC@2x.png'),
        logo('nation', '33-Paypay@2x.png'),
        logo('nation', '34-SCB Planet Plus@2x.png'),
        logo('nation', '35-shopeePay@2x.png'),
        logo('nation', '36-Starryblu@2x.png'),
        logo('nation', '37-Tinaba-APlus@2x.png'),
        logo('nation', '38-Toss Pay@2x.png'),
        logo('nation', "39-Touch 'n Go eWallet-APlus@2x.png"),
        logo('nation', '40-TrueMoney Wallet-APlus@2x.png'),
        logo('nation', '41-Frame 2117130534@2x.png'),
        logo('nation', '42-Wise@2x.png'),
        logo('nation', '43-ZaloPay@2x.png'),
        logo('nation', '44-CPG@2x.png'),
        logo('nation', '45-Duitnow@2x.png'),
        logo('nation', '46-Frame 2147230526@2x.png'),
        logo('nation', '47-HUMO@2x.png'),
        logo('nation', '48-KHQR@2x.png'),
        logo('nation', '49-Lanka Pay@2x.png'),
        logo('nation', '50-CSQ mada@2x.png'),
        logo('nation', '51-Nepal Pay@2x.png'),
        logo('nation', '52-PromptPay@2x.png'),
        logo('nation', '53-QRIS@2x.png'),
        logo('nation', '54-QRPH@2x.png'),
        logo('nation', '55-SG QRPlus@2x.png', 'SG QR+'),
        logo('nation', '56-Transferencias 3.0@2x.png'),
        logo('nation', '57-VietQR Global@2x.png'),
        logo('nation', '58-ZeroPay@2x.png'),
      ],
    },
    {
      key: 'acq',
      scroll: true,
      logos: [
        logo('acq', '1-adyen@2x.png'),
        logo('acq', '2-antom blue@2x.png'),
        logo('acq', '3-ALLINPAY@2x.png'),
        logo('acq', '4-Frame 2117130495@2x.png'),
        logo('acq', '5-fiserv@2x.png'),
        logo('acq', '6-nets@2x.png'),
        logo('acq', '7-nuvei@2x.png'),
        logo('acq', '8-worldpay@2x.png'),
        logo('acq', '9-worldline@2x.png'),
      ],
    },
  ] as PartnerRow[],
  note: 'Partners are listed alphabetically. Listing order does not imply priority or level of participation.',
};

// ─── Open Source Section ─────────────────────────────────────────────────────
export const OPEN_SOURCE = {
  introTitle: 'Build AMP together',
  introDescription:
    'AMP is an open-source, application-layer trust protocol for secure, user-authorized agentic payments, designed to work across existing mobile wallets and payment networks.\n\nWe invite ecosystem partners to explore AMP, share feedback, and build the future of agentic commerce with us. Specifications and reference implementations are available on GitHub.',
  iconSrc: assetUrl('/assets/39EVG3e-opensource.svg'),
  actionCards: [
    {
      icon: assetUrl('/assets/5qszJzf-yueduxieyiguifan.svg'),
      title: 'Learn',
      description:
        'Understand how AMP defines user intent, authorization, and payment execution.',
      href: '#',
      tab: 'agreement' as const,
    },
    {
      icon: assetUrl('/assets/noVMBlY-tiyandianxingchangjing.svg'),
      title: 'Experience',
      description: 'Explore payment journeys to see how AMP works in practice.',
      href: '#scenarios',
    },
    {
      icon: assetUrl('/assets/Aa7kiFW-canyugongxian.svg'),
      title: 'Contribute',
      description: 'Have a suggestion, feedback, or a technical improvement?',
      contactText: 'Contact us at\nalipayplus_amp@ant-intl.com.',
      href: '#',
      isGithubCard: true,
    },
    {
      icon: assetUrl('/assets/JhnSreB-jierushixianzhinan.svg'),
      title: 'Implement',
      description:
        'Visit the GitHub repository to access specifications and reference implementations.',
      href: 'https://github.com/ant-intl/AMP',
      isGithubCard: true,
    },
  ],
  footer: {
    brandName: 'Copyright 2026, Ant International or its affiliates.',
    brandDescription: 'Licensed under the Apache License, Version 2.0.',
    links: [
      { label: 'Specification', href: '#', tab: 'agreement' as const },
      { label: 'GitHub', href: 'https://github.com/ant-intl/AMP' },
    ],
  },
};

// ─── Canvas Config ───────────────────────────────────────────────────────────
export const CODE_RAIN_CONFIG = {
  chars: '01#<>{}[];=+&|!~^01'.split(''),
  cellW: 18,
  cellH: 20,
  fontSize: 13,
  revealRadius: 120,
  fadeRadius: 60,
};
